
import { PartnerController } from "@/app/ui/controllers/PartnerController";
import { ApiStateData } from "@/domain/entities/Api";
import {
  CBPPickup,
  CBPPickupListEntities,
  CBPPickupManifest,
  ShipmentBooking
} from "@/domain/entities/ShipmentBooking";
import { debounce } from "lodash";
import { Options, Vue } from "vue-class-component";
import SelectSearchV2 from "@/app/ui/components/select-search/index.vue";
import { FlagsPermissionCbp } from "@/feature-flags/flags-cbp";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  convertDecimalWithComma,
  ellipsisString,
  formatDateNormal,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { RequestListBooking } from "@/data/payload/api/BookingRequest";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { AccountController } from "@/app/ui/controllers/AccountController";
import copy from "copy-text-to-clipboard";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";

@Options({
  components: {
    SelectSearchV2
  },
  computed: {}
})
export default class TabListBooking extends Vue {
  mounted() {
    this.onResetAdvanceFilter(false);
    this.getListClientCBP();
  }
  get isAbleCreateManifest() {
    return FlagsPermissionCbp.cbp_print_manifest_v2.isEnabled();
  }
  get disabledCreateManifest() {
    return !this.filter.pmcClientId?.name?.length || !this.filter.pmcClientId;
  }
  filter = {
    pmcClientId: "" as any,
    pmcStatus: ""
  };
  apiListData = new ApiStateData();
  listData = new CBPPickupListEntities({});
  onSearchListPosCbp = debounce((search: string) => {
    PartnerController.getClientNamePOS({
      search,
      page: 1,
      limit: 10,
      isBranch: true,
      isApiCbp: true,
      packageType: "",
      cityCode: "",
      isDirectory: false
    });
  }, 250);

  get isLoadingPosCbp() {
    return PartnerController.loadingClientsNamePOS;
  }

  get posCbp() {
    return PartnerController.clientDataNamePOS.data.map((e: any) => ({
      id: e.clientId,
      name: `${e.clientCode} - ${e.clientName}`,
      status: e.clientStatus
    }));
  }

  onSelectPosCbp() {
    Object.assign(this.filter, {
      pmcClientId: this.filter.pmcClientId || ""
    });
    this.onChangeFilter();
  }

  onChangeFilter() {
    this.listData.pagination.page = 1;
    this.getListClientCBP();
  }

  async onPrintManifest() {
    const resp = await this.getDataManifest();
    MainAppController.showLoading();

    try {
      if (!resp?.success) return;

      const print = await import(
        "@/app/ui/views/shipment/booking/manifest/cpb"
      );
      const logo = this.getLogoSrc();
      const data = this.buildManifestData(resp.resp.data);

      print.printmanifestPdf(resp.resp, data, logo);
    } finally {
      MainAppController.closeLoading();
    }
  }

  private getLogoSrc(): string {
    const logoElement = document.getElementById(
      "logo"
    ) as HTMLImageElement | null;
    return logoElement?.src || "";
  }

  private buildManifestData(items: any[]): any[] {
    return items.map((item: any, index: number) => {
      const base = {
        no: index + 1,
        sttNo: "",
        product: "",
        date: "",
        commodity: "",
        koli: "",
        grossWeight: "",
        volumeWeight: "",
        originCity: "",
        destCity: "",
        sttNoRefExternal: ""
      };

      return {
        ...base,
        sttNo: this.appendMultiline(base.sttNo, item.sttNo),
        product: this.appendMultiline(base.product, item.sttProductType),
        date: this.appendMultiline(
          base.date,
          formatDateNormal(item.sttCreatedAt, "DD MMM YYYY")
        ),
        koli: this.appendMultiline(base.koli, item.sttTotalPiece),
        grossWeight: this.appendMultiline(
          base.grossWeight,
          `${item.sttGrossWeight} Kg`
        ),
        originCity: this.appendMultiline(base.originCity, item.sttOriginCity),
        destCity: this.appendMultiline(base.destCity, item.sttDestinationCity)
      };
    });
  }

  private appendMultiline(existing: string, value: any): string {
    return existing ? `${existing}\n\n${value}` : `${value}`;
  }

  async getDataManifest() {
    try {
      MainAppController.showLoading();
      MainAppController.closeErrorMessage();
      const resp = await ShipmentBookingController.getManifestCBP(
        new RequestListBooking({
          pmcClientId: this.filter.pmcClientId?.id || "",
          pmcStatus: this.filter.pmcStatus,
          paymentStatus: "",
          isPaidUnpaid: false,
          cache: true,
          isTotalData: false,
          version: "1"
        })
      );
      return { success: true, resp };
    } catch (error) {
      if ((error as any).response.status === 404) {
        return;
      }
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("bookingCbp.errorPrintManifest"),
          () => this.getDataManifest()
        )
      );
      return { success: false, resp: new CBPPickupManifest() };
    } finally {
      MainAppController.closeLoading();
    }
  }

  get columns() {
    return [
      ...this.getSttAndStatusColumns(),
      ...this.getShipmentDetailColumns(),
      ...this.getExtraInfoColumns()
    ];
  }

  // STT + manifest status columns
  private getSttAndStatusColumns() {
    return [
      {
        key: "actionable_column",
        name: `${this.$t(
          "bookingCbp.columnStt"
        )}<br/><div class='text-14px font-normal text-gray-lp-800'>${this.$t(
          "bookingCbp.columnStatusManifest"
        )}</div>`,
        styleHead: "w-50 text-left whitespace-no-wrap",
        styleBody: "text-red-lp-200 text-center font-montserrat",
        render: (item: CBPPickup) => `
        <div class='pl-1 text-left text-red-lp-200 underline' style="color: #D11F40;">
          ${item.sttNo}
        </div>
        ${this.handleStyleStatusPrint(item)}`,
        showButton: () => true,
        icon: "copy-clipboard",
        iconStyle: "ml-1",
        iconColor: "red-lp-100",
        onClickValue: (item: ShipmentBooking) => {
          const result = copy(item.sttNo);
          if (result) {
            this.setNotification();
          }
        },
        iconTooltip: () => this.$t("bookingCbp.tooltipCopyStt")
      },
      {
        name: this.$t("bookingShipment.list.table.lastStatus"),
        styleHead: "w-40 text-left",
        render: (item: CBPPickup) => {
          const color = this.colorChipLastStatus(item.sttLastStatusId);
          return `<div class="${color.text} flex">
                  <div class="rounded-full capitalize px-2 py-0 ${color.bg}">
                    ${item.sttLastStatusId}
                  </div>
                </div>`;
        }
      }
    ];
  }

  // Shipment detail columns
  private getShipmentDetailColumns() {
    return [
      {
        name: this.$t("bookingShipment.list.table.bookedFor"),
        styleHead: "w-64 text-left whitespace-no-wrap",
        render: (item: CBPPickup) =>
          `<div class="text-gray-lp-300 flex">
          <div class="capitalize">${ellipsisString(item.sttBilledTo, 90)}</div>
        </div>`
      },
      {
        name: this.$t("bookingShipment.list.table.destinationCity"),
        styleHead: "w-52 text-left",
        render: (item: CBPPickup) =>
          `<div class="break-all text-gray-lp-300">
          ${item.sttDestinationCity} - ${item.sttDestinationCityName}
        </div>`
      },
      {
        name: this.$t("bookingShipment.list.table.serviceType"),
        styleHead: "w-50 text-left whitespace-nowrap",
        render: (item: CBPPickup) =>
          `<div class="text-gray-lp-300 flex">${
            item.sttProductType === "INTERPACKMAXI" 
              ? "INTERPACK MAXI"
              : item.sttProductType
          }</div>`
      },
      {
        name: this.$t("bookingShipment.list.table.totalPieces"),
        styleHead: "w-32 text-left whitespace-nowrap",
        render: (item: CBPPickup) =>
          `<div class='flex text-gray-lp-300'>${item.sttTotalPiece}</div>`
      },
      {
        name: this.$t("bookingShipment.list.table.totalWeight"),
        styleHead: "w-40 text-left",
        render: (item: CBPPickup) =>
          `<div class='flex text-gray-lp-300'>${convertDecimalWithComma(
            item.sttChargeAbleWeight,
            2
          )} kg</div>`
      }
    ];
  }

  // Extra info columns
  private getExtraInfoColumns() {
    return [
      {
        name: this.$t("bookingShipment.list.table.commodity"),
        styleHead: "w-64 text-left",
        render: (item: CBPPickup) => `
        <div class="flex flex-row items-center space-x-1">
          <div class="text-gray-lp-300 flex">
            ${ellipsisString(item.sttCommodityName, 25)}
          </div>
          ${this.handleLabelQuarantineAndDG(item)}
        </div>`
      },
      {
        name: this.$t("bookingShipment.list.table.bookingDate"),
        styleHead: "w-64 text-left",
        render: (item: CBPPickup) => {
          const locale = this.$i18n.locale === "my" ? "ms" : this.$i18n.locale;
          const formatted = formatDate(
            item.sttCreatedAt,
            "DD MMMM YYYY, HH:mm",
            true,
            locale,
            false
          );
          return `
        <div class="text-gray-lp-300">${formatted}</div>
        <div class="capitalize text-gray-lp-500">${ellipsisString(
          item.sttCreatedName,
          30
        )}</div>`;
        }
      }
    ];
  }

  get isEmpty(): boolean {
    return !this.listData.data.length;
    // add no filter
  }

  async getListClientCBP() {
    this.apiListData.loading = true;
    try {
      const resp = await ShipmentBookingController.getBookingListCBP(
        new RequestListBooking({
          page: this.listData.pagination.page,
          pmcClientId: this.filter.pmcClientId?.id || "",
          pmcStatus: this.filter.pmcStatus,
          limit: 20,
          paymentStatus: "",
          isPaidUnpaid: false,
          cache: true,
          isTotalData: true,
          version: "1"
        })
      );
      this.listData = new CBPPickupListEntities({
        pagination: new PaginationV2({
          page: resp.pagination.page,
          limit: resp.pagination.limit,
          totalData: resp.pagination.totalData
        }),
        data: resp.data
      });
      this.apiListData.errorType = "";
    } catch (error) {
      this.apiListData.errorType = parsingErrorResponse(error).type;
    } finally {
      this.apiListData.loading = false;
    }
  }

  handleStyleStatusPrint(item: CBPPickup) {
    const unprinted = this.$t("bookingCbp.statusUnprinted");
    const printed = this.$t("bookingCbp.statusPrinted");
    if (item.pmcStatus.toLowerCase() === "unprinted")
      return `<div class='text-left mt-1 rounded-full flex flex-row items-center bg-yellow-lp-200 text-yellow-lp-100 p-1 space-x-1'> <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12ZM6.66667 3.33333C6.66667 2.96514 6.36819 2.66667 6 2.66667C5.63181 2.66667 5.33333 2.96514 5.33333 3.33333V6.33333C5.33333 6.70152 5.63181 7 6 7H8.66667C9.03486 7 9.33333 6.70152 9.33333 6.33333C9.33333 5.96514 9.03486 5.66667 8.66667 5.66667H6.66667V3.33333Z' fill='#F09035'/></svg>${unprinted}</div>`;
    return `<div class='text-left mt-1 rounded-full flex flex-row items-center bg-green-lp-500 text-green-lp-200 p-1 space-x-1'> <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'><path fill-rule='evenodd' clip-rule='evenodd' d='M10 18.0001C12.1217 18.0001 14.1566 17.1572 15.6569 15.6569C17.1571 14.1566 18 12.1218 18 10.0001C18 7.87833 17.1571 5.8435 15.6569 4.34321C14.1566 2.84292 12.1217 2.00006 10 2.00006C7.87827 2.00006 5.84344 2.84292 4.34315 4.34321C2.84285 5.8435 2 7.87833 2 10.0001C2 12.1218 2.84285 14.1566 4.34315 15.6569C5.84344 17.1572 7.87827 18.0001 10 18.0001ZM13.707 8.70706C13.8892 8.51846 13.99 8.26586 13.9877 8.00366C13.9854 7.74146 13.8802 7.49065 13.6948 7.30524C13.5094 7.11983 13.2586 7.01467 12.9964 7.01239C12.7342 7.01011 12.4816 7.1109 12.293 7.29306L9 10.5861L7.707 9.29306C7.5184 9.1109 7.2658 9.01011 7.0036 9.01239C6.7414 9.01467 6.49059 9.11983 6.30518 9.30524C6.11977 9.49065 6.0146 9.74146 6.01233 10.0037C6.01005 10.2659 6.11084 10.5185 6.293 10.7071L8.293 12.7071C8.48053 12.8945 8.73484 12.9998 9 12.9998C9.26516 12.9998 9.51947 12.8945 9.707 12.7071L13.707 8.70706Z' fill='#006644'/></svg>${printed}</div>`;
  }

  colorChipLastStatus(status: string) {
    const statusMap: any = {
      POD: { bg: "bg-blue-lp-200", text: "text-blue-lp-300" },
      CNX: { bg: "bg-red-lp-600", text: "text-red-lp-100" },
      "STI Dest": { bg: "bg-red-lp-600", text: "text-red-lp-100" }
    };
    return (
      statusMap[status] || { bg: "bg-gray-lp-400", text: "text-gray-lp-300" }
    );
  }

  notification = false;
  setNotification() {
    this.notification = false;

    setTimeout(() => {
      this.notification = true;
    }, 10);
  }

  handleLabelQuarantineAndDG(data: CBPPickup) {
    if (data.isQuarantine && data.isDangerousGoods) {
      return "<div class='bg-red-lp-600 text-red-lp-100 px-1 rounded'>DG</div><div class='bg-yellow-lp-200 text-yellow-lp-100 px-1 rounded'>K</div>";
    }
    if (data.isQuarantine) {
      return "<div class='bg-yellow-lp-200 text-yellow-lp-100 px-1 rounded'>K</div>";
    }
    if (data.isDangerousGoods) {
      return "<div class='bg-red-lp-200 text-red-lp-100 px-1 rounded'>DG</div>";
    }
    return "";
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get roleAccount() {
    return (
      (this.dataProfile.account_type === "partner"
        ? this.dataProfile.account_type_detail.type
        : this.dataProfile.account_type) || "internal"
    );
  }

  async onResetAdvanceFilter(callList = true) {
    this.apiListData.loading = true;
    Object.assign(this.filter, {
      pmcClientId: "" as any,
      pmcStatus: ""
    });
    this.listData.pagination.page = 1;
    if (callList) await this.getListClientCBP();
  }

  get statusPrintManifest() {
    return [
      { label: this.$t("bookingCbp.statusPrinted"), value: "printed" },
      { label: this.$t("bookingCbp.statusUnprinted"), value: "unprinted" }
    ];
  }

  changeStatusPrintManifest(value: any) {
    if (value === this.filter.pmcStatus) {
      Object.assign(this.filter, {
        pmcStatus: ""
      });
      this.onChangeFilter();
      return;
    }
    Object.assign(this.filter, {
      pmcStatus: value || ""
    });
    this.onChangeFilter();
  }

  tabKeyActive = "";

  isTabActive(key: string) {
    return key === this.filter.pmcStatus;
  }

  get copyStyles() {
    return {
      background: "bg-gray-lp-300",
      text: "text-white",
      message: this.$t("bookingCbp.copySttSuccess"),
      width: "w-300px"
    };
  }
}
